import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const NotificationBell = ({ onNotificationClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  useEffect(() => {
    loadNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      loadNotifications(true); // Silent reload
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/notifications/list.php?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Filter out capacity_warning notifications
        const filteredNotifications = response.data.data.notifications.filter(
          notification => notification.type !== 'capacity_warning'
        );
        setNotifications(filteredNotifications);
        
        // Calculate unread count from filtered notifications (excluding capacity warnings)
        const unreadFiltered = filteredNotifications.filter(n => 
          n.is_read === '0' || n.is_read === 0
        );
        
        // Set unread count
        const count = unreadFiltered.length;
        setUnreadCount(count);
      }
    } catch (error) {
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async () => {
    setAnchorEl(null);
    
    // Mark all notifications as read when closing the menu
    if (unreadCount > 0) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_BASE_URL}/notifications/mark_read.php`,
          { mark_all: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Refresh notifications to update unread count
        loadNotifications(true);
      } catch (error) {
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/notifications/mark_read.php`,
        { notification_id: notificationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      loadNotifications(true);
    } catch (error) {
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/notifications/mark_read.php`,
        { mark_all: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      loadNotifications();
    } catch (error) {
    }
  };

  const handleNotificationClick = async (notification) => {
    // Close the menu (this will mark all as read)
    handleClose();

    // Navigate based on notification type
    if (notification.type === 'booking' && notification.related_booking_id) {
      // Navigate to bookings page and pass the booking ID to open the modal
      navigate('/bookings', { 
        state: { 
          openBookingId: notification.related_booking_id 
        } 
      });
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'info';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'booking': return '🎫';
      case 'system_alert': return '🔔';
      case 'cancellation': return '❌';
      default: return 'ℹ️';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error">
          {unreadCount > 0 ? (
            <NotificationsActiveIcon sx={{ animation: 'pulse 2s infinite' }} />
          ) : (
            <NotificationsIcon />
          )}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 600 }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Benachrichtigungen</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<MarkEmailReadIcon />}
              onClick={markAllAsRead}
            >
              Alle gelesen
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Keine Benachrichtigungen
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  backgroundColor: (notification.is_read === '0' || notification.is_read === 0) ? 'action.hover' : 'transparent',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.selected' },
                  borderLeft: (notification.is_read === '0' || notification.is_read === 0) ? '4px solid' : 'none',
                  borderColor: `${getSeverityColor(notification.severity)}.main`,
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{getTypeIcon(notification.type)}</span>
                      <Typography variant="body2" fontWeight={(notification.is_read === '0' || notification.is_read === 0) ? 'bold' : 'normal'}>
                        {notification.title}
                      </Typography>
                      <Chip
                        label={notification.type}
                        size="small"
                        color={getSeverityColor(notification.severity)}
                        sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {formatTime(notification.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Menu>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}
      </style>
    </>
  );
};

export default NotificationBell;

